import React, { useEffect, useState, useRef } from 'react';
import styled, { css } from 'styled-components';
import { basicColor, radius, slideIn } from '@/styles/template';


const Wrap = styled.div<{ isSticky: boolean }>`
    position: relative;
    bottom: 0;
    padding: 2rem 0 0;
    left: 0;
    right: 0;
    margin: auto;
    width: fit-content;
    transition: top 0.3s;
    
    ${({ isSticky }) => isSticky && css`
        position: fixed;
        padding: 1rem;
        z-index: 4;
        animation: ${slideIn} .5s ease-in-out;
        & button {
            background: ${basicColor.primaryLight};
            box-shadow: ${props => props.theme.boxShadow.Middle};
            &::before {
                border-color: transparent;
            }

            &::after {
               background-color: transparent;
            }
        }
    `}
`;

const InnerWrap = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
`

const Placeholder = styled.div<{ height: number }>`
  height: ${props => props.height}px; /* Высота компонента для предотвращения "прыжка" контента */
`;

type StickyProps = {
    children: React.ReactNode;
    triggerRef: React.RefObject<HTMLElement>;
};

const StickyContainer: React.FC<StickyProps> = ({ children, triggerRef }) => {
    const [isSticky, setIsSticky] = useState(false);
    const [placeholderHeight, setPlaceholderHeight] = useState(0);
    const [lastScrollTop, setLastScrollTop] = useState(0);
    const ref = useRef<HTMLDivElement>(null);

    const handleScroll = () => {
        const scrollTop = getCurrentScroll();
        const isScrollingUp = scrollTop < lastScrollTop;
        const triggerOffsetTop = triggerRef.current
            ? triggerRef.current.getBoundingClientRect().top + window.pageYOffset
            : 0;


        if (isScrollingUp && scrollTop > triggerOffsetTop && !isSticky) {
            setPlaceholderHeight(ref.current?.offsetHeight || 0);
            setIsSticky(true);
        } else if (isScrollingUp && scrollTop <= triggerOffsetTop && isSticky) {
            setIsSticky(false);
        } else if (!isScrollingUp && isSticky) {
            setIsSticky(true);
        }

        setLastScrollTop(scrollTop <= 0 ? 0 : scrollTop);
    };

    useEffect(() => {
        window.addEventListener('scroll', handleScroll);
        return () => {
            window.removeEventListener('scroll', handleScroll);
        };
    }, [isSticky, lastScrollTop]);

    return (
        <>
            <Placeholder height={isSticky ? placeholderHeight : 0} />
            <Wrap ref={ref} isSticky={isSticky}>
                <InnerWrap>
                    {children}
                </InnerWrap>
            </Wrap>
        </>
    );
};

function getCurrentScroll() {
    return window.pageYOffset || document.documentElement.scrollTop;
}

export default StickyContainer;
